import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { PremiumSnapshot } from '../services/tradeSignalsService';

interface OptionPremiumChartProps {
  premiumHistory: PremiumSnapshot[];
  entryPremium: number;
  targetPremium: number;
  stopPremium: number;
  isSelling: boolean;
  symbol: string;
}

// All chart times rendered in ET — premium snapshots are tied to market
// session hours and the "all times Eastern" rule applies. Without timeZone,
// formatters fall back to browser-local which can drift labels by a full
// hour for users outside US/Eastern.
const ET = 'America/New_York';

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString('en-US', { timeZone: ET, month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' });
  } catch {
    return ts;
  }
}

function formatXTick(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    // Include minutes — premium snapshots are taken every 30 min, so an
    // hour-only label produces duplicates ("5/1 4 PM" appearing twice for
    // 4:00 and 4:30, "5/1 5 PM" appearing twice for 5:00 and 5:30, etc.).
    const date = d.toLocaleDateString('en-US', { timeZone: ET, month: 'numeric', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date} ${time}`;
  } catch {
    return '';
  }
}

class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '0.75rem 1.5rem',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          fontSize: '0.8em',
          color: '#64748b'
        }}>
          Premium chart unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

const OptionPremiumChartInner: React.FC<OptionPremiumChartProps> = ({
  premiumHistory,
  entryPremium,
  targetPremium,
  stopPremium,
  isSelling,
  symbol,
}) => {
  if (!premiumHistory || premiumHistory.length === 0) return null;

  const gradientId = `premiumGrad-${symbol}`;

  const data = premiumHistory.map(p => ({
    ts: p.timestamp,
    mark: typeof p.mark === 'number' && isFinite(p.mark) ? p.mark : 0,
    bid: typeof p.bid === 'number' && isFinite(p.bid) ? p.bid : 0,
    ask: typeof p.ask === 'number' && isFinite(p.ask) ? p.ask : 0,
    delta: p.delta,
    theta: p.theta,
  }));

  const marks = data.map(d => d.mark).filter(m => m > 0);
  if (marks.length === 0) return null;

  const allValues = [...marks, entryPremium, targetPremium, stopPremium].filter(v => v > 0);
  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const range = maxVal - minVal;
  const padding = range > 0 ? range * 0.12 : maxVal * 0.05;
  const yMin = Math.max(0, Math.floor((minVal - padding) * 100) / 100);
  const yMax = Math.ceil((maxVal + padding) * 100) / 100;

  // ~6 x-axis ticks
  const tickIndices: number[] = [];
  const step = Math.max(1, Math.floor(data.length / 6));
  for (let i = 0; i < data.length; i += step) tickIndices.push(i);
  const ticks = tickIndices.map(i => data[i].ts);

  // Color logic: for sells, green when premium is decaying (mark < entry = profit)
  // For buys, green when premium is rising (mark > entry = profit)
  const lastMark = marks[marks.length - 1];
  const isProfit = isSelling
    ? lastMark < entryPremium
    : lastMark > entryPremium;
  const areaColor = isProfit ? '#22c55e' : '#ef4444';

  return (
    <div style={{
      marginTop: '1rem',
      padding: '0.75rem',
      backgroundColor: 'rgba(255,255,255,0.03)',
      borderRadius: '8px',
      border: '1px solid rgba(255,255,255,0.08)'
    }}>
      <div style={{ fontSize: '0.8em', color: '#94a3b8', marginBottom: '0.5rem' }}>
        Option Premium (30-min snapshots)
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={areaColor} stopOpacity={0.3} />
              <stop offset="100%" stopColor={areaColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="ts"
            ticks={ticks}
            tickFormatter={formatXTick}
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            tickLine={false}
          />
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `$${v.toFixed(2)}`}
            width={55}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null;
              const d = payload[0].payload;
              return (
                <div style={{
                  backgroundColor: '#1e293b',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.85em',
                  color: '#e2e8f0'
                }}>
                  <div style={{ marginBottom: '0.25rem', color: '#94a3b8' }}>
                    {formatTimestamp(label as string)}
                  </div>
                  <div style={{ fontWeight: 'bold' }}>
                    Mark: <span style={{ color: '#60a5fa' }}>${d.mark?.toFixed(2)}</span>
                  </div>
                  <div>
                    Bid/Ask: ${d.bid?.toFixed(2)} / ${d.ask?.toFixed(2)}
                    <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>
                      (spread ${(d.ask - d.bid)?.toFixed(2)})
                    </span>
                  </div>
                  {d.delta != null && (
                    <div style={{ color: '#94a3b8' }}>
                      Delta: {d.delta?.toFixed(3)}
                    </div>
                  )}
                </div>
              );
            }}
          />
          <ReferenceLine
            y={entryPremium}
            stroke="#3b82f6"
            strokeDasharray="4 4"
            label={{ value: 'Entry', fill: '#3b82f6', fontSize: 10, position: 'left' }}
          />
          <ReferenceLine
            y={targetPremium}
            stroke="#22c55e"
            strokeDasharray="4 4"
            label={{ value: 'Target', fill: '#22c55e', fontSize: 10, position: 'left' }}
          />
          <ReferenceLine
            y={stopPremium}
            stroke="#ef4444"
            strokeDasharray="4 4"
            label={{ value: 'Stop', fill: '#ef4444', fontSize: 10, position: 'left' }}
          />
          <Area
            type="monotone"
            dataKey="mark"
            stroke={areaColor}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 3, fill: areaColor }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const OptionPremiumChart: React.FC<OptionPremiumChartProps> = (props) => (
  <ChartErrorBoundary>
    <OptionPremiumChartInner {...props} />
  </ChartErrorBoundary>
);
